import { useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Megaphone, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VideoPreview } from "./VideoPreview";
import { linkVideoToCampaign } from "@/lib/api";

const STATUS = {
  approved: { label: "Approvato · pronto", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
  in_campaign: { label: "In campagna", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" },
};

/** Reel approved in Video / Reel Studio, shown inside the campaign card. */
export function CampaignReel({ project, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!project || !STATUS[project.status]) return null;
  const duration = (project.scenes || []).reduce((a, s) => a + (s.duration || 3), 0);
  const st = STATUS[project.status];

  const use = async () => {
    setBusy(true);
    try {
      const p = await linkVideoToCampaign();
      onChange?.(p);
      toast.success("Reel collegato alla campagna");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-violet-500/20 bg-violet-500/[0.03] p-5" data-testid="campaign-reel">
      <div className="flex flex-col md:flex-row gap-5">
        <div className="shrink-0 flex justify-center md:justify-start">
          <VideoPreview project={project} compact />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/25 text-violet-300 flex items-center justify-center"><Clapperboard size={15} /></span>
            <h3 className="font-heading text-lg font-semibold text-slate-100">Reel pronto da Nadia</h3>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${st.cls}`} data-testid="campaign-reel-status">{st.label}</span>
          </div>
          <p className="text-sm text-slate-300 mb-3">{project.idea}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
            <Info label="Formato" value={`${project.format_label} ${project.ratio}`} testId="campaign-reel-format" />
            <Info label="Durata" value={`${duration} s · ${(project.scenes || []).length} scene`} testId="campaign-reel-duration" />
            <Info label="CTA finale" value={project.cta} testId="campaign-reel-cta" />
            <Info label="Rendering" value={project.render?.mock ? "Video non generato" : project.render?.status || "-"} />
          </div>
          {project.status === "in_campaign" ? (
            <div className="inline-flex items-center gap-2 text-sm text-cyan-300" data-testid="campaign-reel-linked"><Check size={15} /> Collegato a questa campagna · placement Reel attivo</div>
          ) : (
            <Button onClick={use} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold" data-testid="campaign-reel-use">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Megaphone size={14} className="mr-2" />} Usa questo Reel nella campagna
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-200 font-medium">{value}</div>
    </div>
  );
}
